// Help content for all modules
// Easy to update and maintain in one place

export const helpContent = {
  // ============================================
  // DASHBOARD
  // ============================================
  'dashboard': {
    title: 'Dashboard',
    icon: '📊',
    description: 'Your central hub for monitoring Google Ads performance across all connected accounts.',
    features: [
      'Real-time performance metrics (spend, clicks, conversions, CPA, ROAS)',
      'Trend charts showing performance over time',
      'Top and bottom performing campaigns',
      'Budget tracking and alerts',
      'Quick access to all optimization tools'
    ],
    faqs: [
      {
        question: 'How often is the data updated?',
        answer: 'Dashboard data is refreshed every 15 minutes. You can also manually refresh using the refresh button in the top right.'
      },
      {
        question: 'Why do I see different numbers than in Google Ads?',
        answer: 'There may be a slight delay (up to 3 hours) between Google Ads and our dashboard. Also, we may apply different attribution windows. For the most accurate real-time data, check Google Ads directly.'
      },
      {
        question: 'Can I filter by specific campaigns?',
        answer: 'Yes! Use the language filter to narrow down results, or switch between different connected accounts using the account selector in the sidebar.'
      }
    ]
  },

  // ============================================
  // BID OPTIMIZER
  // ============================================
  'bid-optimizer': {
    title: 'Bid Optimizer',
    icon: '📈',
    description: 'AI-powered recommendations to optimize your bids and maximize ROI. Get actionable suggestions based on your campaign performance data.',
    features: [
      'Device bid adjustments (mobile, desktop, tablet, connected TV)',
      'Keyword bid recommendations',
      'Negative keyword suggestions to reduce wasted spend',
      'Day/time parting recommendations',
      'One-click apply directly to Google Ads'
    ],
    faqs: [
      {
        question: 'What does "High Priority" mean?',
        answer: 'High priority recommendations have the highest potential impact on your account. They are based on significant performance differences and high confidence data. We recommend addressing these first.'
      },
      {
        question: 'How are bid adjustments calculated?',
        answer: 'We analyze your CPA/ROAS targets against actual performance. If a device, keyword, or time period significantly outperforms or underperforms your target, we suggest appropriate bid adjustments (typically 15-40%).'
      },
      {
        question: 'What happens when I click "Apply"?',
        answer: 'The optimization is applied directly to your Google Ads account. For bid adjustments, we modify the campaign settings. For negative keywords, we add them to the appropriate level (campaign or ad group).'
      },
      {
        question: 'Can I undo an applied optimization?',
        answer: 'Not directly through Bidoryx, but you can always adjust the settings in Google Ads. We recommend reviewing changes in Google Ads after applying.'
      },
      {
        question: 'Why can\'t I apply device bid adjustments?',
        answer: 'Device bid adjustments only work with manual or enhanced CPC bidding strategies. Campaigns using fully automated bidding (Target CPA, Maximize Conversions, etc.) don\'t allow manual device modifiers.'
      }
    ]
  },

  // ============================================
  // QUALITY SCORE OPTIMIZER
  // ============================================
  'quality-score': {
    title: 'Quality Score Optimizer',
    icon: '⭐',
    description: 'Improve your Quality Scores to lower CPCs and improve ad positions. Get AI-generated suggestions for better ad copy and landing pages.',
    features: [
      'Quality Score analysis per keyword',
      'AI-generated headline and description improvements',
      'Expected CTR, Ad Relevance, and Landing Page Experience breakdown',
      'Historical Quality Score tracking',
      'Language-aware suggestions (matches your campaign language)'
    ],
    faqs: [
      {
        question: 'What is Quality Score?',
        answer: 'Quality Score is Google\'s rating (1-10) of the quality and relevance of your keywords, ads, and landing pages. Higher scores lead to lower costs and better ad positions.'
      },
      {
        question: 'How does AI improvement generation work?',
        answer: 'We use GPT-4 to analyze your current ad copy and generate improved versions. The AI considers your keyword themes, existing ad text, and best practices for Google Ads.'
      },
      {
        question: 'Why are improvements generated in a different language?',
        answer: 'The AI automatically detects the language of your campaign and generates improvements in the same language. If detection fails, it defaults to English.'
      },
      {
        question: 'What\'s a good Quality Score to aim for?',
        answer: 'Generally, 7+ is considered good, 8-10 is excellent. Focus on improving keywords with scores below 6, as these are likely costing you more per click.'
      }
    ]
  },

  // ============================================
  // OFFER ANALYZER
  // ============================================
  'offer-analyzer': {
    title: 'Offer Analyzer',
    icon: '💡',
    description: 'Analyze your website and business to get AI-powered marketing insights and campaign suggestions.',
    features: [
      'Website content analysis',
      'USP (Unique Selling Proposition) extraction',
      'Competitor positioning insights',
      'Target audience suggestions',
      'Campaign angle recommendations'
    ],
    faqs: [
      {
        question: 'How does website analysis work?',
        answer: 'We scrape your website content (text, headlines, meta descriptions) and use AI to understand your business, products, and value propositions.'
      },
      {
        question: 'Is my website data stored?',
        answer: 'Website analysis results are cached temporarily to improve performance. We don\'t permanently store or share your website content.'
      }
    ]
  },

  // ============================================
  // AI CAMPAIGN GENERATOR
  // ============================================
  'ai-generator': {
    title: 'AI Campaign Generator',
    icon: '✨',
    description: 'Create complete Google Ads campaigns in minutes using AI. Generate keywords, ad copy, and campaign structure automatically.',
    features: [
      'Complete campaign structure generation',
      'AI-powered keyword research',
      'Ad copy generation with multiple variants',
      'Automatic ad group organization',
      'Export ready for Google Ads'
    ],
    faqs: [
      {
        question: 'How accurate are the AI-generated keywords?',
        answer: 'AI suggestions are a great starting point but should be reviewed. We recommend checking search volumes and relevance before launching.'
      },
      {
        question: 'Can I edit the generated campaigns?',
        answer: 'Yes! All generated content is fully editable before you export or create the campaign.'
      }
    ]
  },

  // ============================================
  // CAMPAIGN BUILDER
  // ============================================
  'campaign-builder': {
    title: 'Campaign Builder',
    icon: '🎯',
    description: 'Build and manage Google Ads campaigns with an intuitive interface. Create campaigns, ad groups, and ads without leaving Bidoryx.',
    features: [
      'Visual campaign structure builder',
      'Bulk keyword upload',
      'Ad copy templates',
      'Budget and bidding settings',
      'Direct publish to Google Ads'
    ],
    faqs: [
      {
        question: 'Can I edit existing campaigns?',
        answer: 'Currently, Campaign Builder is focused on creating new campaigns. For editing existing campaigns, use Google Ads directly or our Bid Optimizer for bid adjustments.'
      }
    ]
  },

  // ============================================
  // KEYWORD RESEARCH
  // ============================================
  'keyword-research': {
    title: 'Keyword Research',
    icon: '🔍',
    description: 'Discover high-performing keywords for your campaigns. Get search volume, competition, and CPC estimates.',
    features: [
      'Keyword ideas from seed keywords or URLs',
      'Search volume and trend data',
      'Competition level indicators',
      'CPC estimates',
      'Export to Campaign Builder'
    ],
    faqs: [
      {
        question: 'Where does the keyword data come from?',
        answer: 'We use the Google Ads Keyword Planner API to get official search volume and competition data directly from Google.'
      },
      {
        question: 'How accurate are CPC estimates?',
        answer: 'CPC estimates are based on Google\'s data and represent average costs. Actual CPCs may vary based on your Quality Score, competition, and other factors.'
      }
    ]
  },

  // ============================================
  // AD CREATOR
  // ============================================
  'ad-creator': {
    title: 'Ad Creator',
    icon: '📝',
    description: 'Create compelling Google Ads with AI assistance. Generate headlines, descriptions, and preview your ads.',
    features: [
      'AI-powered ad copy generation',
      'Character count validation',
      'Ad preview (desktop and mobile)',
      'Multiple ad variants',
      'RSA (Responsive Search Ad) support'
    ],
    faqs: [
      {
        question: 'How many headlines and descriptions should I use?',
        answer: 'Google recommends at least 8-10 headlines and 3-4 descriptions for Responsive Search Ads. More variants give Google more options to optimize.'
      }
    ]
  },

  // ============================================
  // SETTINGS
  // ============================================
  'settings': {
    title: 'Settings',
    icon: '⚙️',
    description: 'Manage your account settings, connected Google Ads accounts, optimization preferences, and billing.',
    features: [
      'Connect/disconnect Google Ads accounts',
      'Set default CPA/ROAS targets per account',
      'Configure notification preferences',
      'Manage subscription and billing',
      'Theme preferences (dark/light mode)'
    ],
    faqs: [
      {
        question: 'How do I connect another Google Ads account?',
        answer: 'Go to Settings > Connected Accounts and click "Link New Account". You\'ll be redirected to Google to authorize access.'
      },
      {
        question: 'What happens if I disconnect an account?',
        answer: 'Disconnecting removes Bidoryx\'s access to that account. Your Google Ads data remains unchanged, but you won\'t see it in Bidoryx anymore.'
      },
      {
        question: 'How do I change my subscription plan?',
        answer: 'Go to Settings > Billing or click on your profile menu and select "Subscription". You can upgrade, downgrade, or manage your payment method there.'
      }
    ]
  }
};

// General FAQ items (not module-specific)
export const generalFaqs = [
  {
    question: 'Is my Google Ads data secure?',
    answer: 'Yes. We use OAuth 2.0 for secure authentication and never store your Google Ads credentials. All data is encrypted in transit and at rest. We only access the data necessary to provide our services.'
  },
  {
    question: 'Can Bidoryx make changes to my Google Ads without my permission?',
    answer: 'No. Every optimization requires you to click "Apply" before any changes are made. We never make automatic changes to your campaigns without explicit approval.'
  },
  {
    question: 'What Google Ads account types are supported?',
    answer: 'We support both individual Google Ads accounts and MCC (Manager) accounts. When you connect an MCC, you can access all child accounts within it.'
  },
  {
    question: 'How do I cancel my subscription?',
    answer: 'Go to Settings > Billing and click "Manage Subscription". You can cancel anytime. Your access continues until the end of your current billing period.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a 7-day free trial so you can evaluate the service. After that, we don\'t offer refunds for partial months, but you can cancel anytime to prevent future charges.'
  },
  {
    question: 'How do I contact support?',
    answer: 'Email us at support@bidoryx.com. We typically respond within 24 hours on business days.'
  }
];

// Quick tips shown randomly or in onboarding
export const quickTips = [
  'Use the Bid Optimizer daily to catch new optimization opportunities.',
  'Set your target CPA/ROAS in Settings to get more accurate recommendations.',
  'Quality Scores above 7 can significantly reduce your cost per click.',
  'Check dismissed recommendations periodically - market conditions change!',
  'Connect all your Google Ads accounts to get a complete performance overview.',
  'Use the language filter in Dashboard to analyze specific market segments.',
  'Export keyword research directly to Campaign Builder to save time.',
  'Review AI-generated ad copy before publishing - it\'s a starting point, not final.',
];

export default helpContent;
